import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Bell, Info } from "lucide-react";

const DEFAULT_TPL = "Ciao {owner_name}! Ti ricordiamo l'appuntamento di {pet_name} il {date} alle {time} presso {store_name}, {store_address}. A presto!";

export default function ReminderSettings() {
  const [cfg, setCfg] = useState({
    auto_send: false,
    hours_before: 24,
    provider: "wa_deeplink",
    reminder_template: DEFAULT_TPL,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/tenant/me").then((r) => {
      const nc = r.data?.notification_config || {};
      setCfg({
        auto_send: !!nc.auto_send,
        hours_before: Number(nc.hours_before) === 48 ? 48 : 24,
        provider: nc.provider || "wa_deeplink",
        reminder_template: nc.reminder_template || DEFAULT_TPL,
      });
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.get("/tenant/me");
      const prev = r.data?.notification_config || {};
      await api.patch("/tenant/branding", { notification_config: { ...prev, ...cfg } });
      toast.success("Reminder salvati");
    } catch (e) {
      toast.error(formatApiError(e, "Errore salvataggio"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-3xl" data-testid="reminder-settings-page">
      <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Appuntamenti</div>
      <h1 className="font-heading text-4xl font-bold tracking-tight mb-2 flex items-center gap-2">
        <Bell className="w-8 h-8" /> Reminder automatici
      </h1>
      <p className="text-velta-mute mb-8">Attiva i promemoria WhatsApp/SMS prima dell&apos;appuntamento. Lo scheduler interno li invia da solo.</p>

      <div className="bg-white border border-velta-border rounded-md p-6 mb-6">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div>
            <div className="font-semibold">Invio automatico</div>
            <div className="text-sm text-velta-mute">ON = reminder in coda; OFF = solo invio manuale dalla agenda</div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={cfg.auto_send}
            data-testid="reminder-auto-switch"
            onClick={() => setCfg((c) => ({ ...c, auto_send: !c.auto_send }))}
            className={`relative h-8 w-14 shrink-0 rounded-full transition ${cfg.auto_send ? "bg-[#2F9E77]" : "bg-zinc-300"}`}
          >
            <span className={`absolute top-1 left-1 h-6 w-6 rounded-full bg-white shadow transition ${cfg.auto_send ? "translate-x-6" : ""}`} />
          </button>
        </div>

        <div className="mb-6">
          <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Preavviso</div>
          <div className="flex gap-2">
            {[24, 48].map((h) => (
              <button
                key={h}
                type="button"
                data-testid={`reminder-hours-${h}`}
                onClick={() => setCfg((c) => ({ ...c, hours_before: h }))}
                className={`h-11 px-5 rounded-md text-sm font-medium border-2 ${cfg.hours_before === h ? "border-velta-ink bg-velta-ink text-white" : "border-velta-border bg-white"}`}
              >
                {h} ore prima
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          data-testid="reminder-save-btn"
          disabled={saving}
          onClick={save}
          className="h-12 px-6 bg-[#1E2D5B] text-white rounded-md font-medium disabled:opacity-50"
        >
          {saving ? "Salvataggio…" : "Salva impostazioni"}
        </button>
      </div>

      <div className="rounded-md border border-sky-200 bg-sky-50 p-5 text-sm text-sky-950" data-testid="reminder-howto">
        <div className="flex items-start gap-2 font-semibold mb-2">
          <Info className="w-4 h-4 mt-0.5 shrink-0" /> Come attivarlo in autonomia
        </div>
        <ol className="list-decimal pl-5 space-y-1.5 text-sky-900/90">
          <li>Metti lo switch su ON e scegli 24h o 48h, poi Salva.</li>
          <li>In ogni appuntamento indica telefono e consenso WhatsApp del proprietario.</li>
          <li>Per l&apos;invio gratis usa WhatsApp Manuale (link wa.me) in Branding → Notifiche. Per l&apos;invio automatico completo configura Meta Cloud o Twilio lì.</li>
          <li>Il sistema scansiona gli appuntamenti ogni pochi minuti e invia solo se ON e se il reminder non è già partito.</li>
        </ol>
      </div>
    </div>
  );
}
